import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Button, Modal, ModalHeader, ModalBody } from 'reactstrap'
import { uplayActions } from '../../redux/actions'
import FormattedMessage from '../FormattedMessage/FormattedMessage'
import Loader from '../Loader/Loader'
import MyRewardsErrorModal from '../Modal/MyRewardsErrorModal/MyRewardsErrorModal'
import MyRewardsSuccessModal from '../Modal/MyRewardsSuccessModal/MyRewardsSuccessModal'

import './UplayIntegration.scss'

const uplayAuthTimeout = 30000

class UplayIntegration extends React.Component {
  constructor(props) {
    super(props)
    this.uplayAuthTimeout = null
    this.uplayAuthAttemptId = 0
    this.state = {
      modal: false,
      initialized: false,
      loading: true,
      uplayError: false,
      uplayErrorString: null,
      uplaySuccess: false,
      redeemedLocally: false,
      myAppId: '1cf20d7b-9a1a-41ad-8dba-074a19b40b8c',
      myGenomeId: '9760f893-5161-4f13-847e-a2a37ae5b133',
      myNextUrl: `${encodeURI(document.location.protocol)}//${encodeURI(
        document.location.host
      )}/uplay-redirect`,
    }
  }

  componentDidMount() {
    const { checkAuthenticated } = this.props
    checkAuthenticated().then(() => this.setState({ loading: false }))
    this.handleUplayMessage = e => {
      if (e.data === 'uplayRedirect') {
        this.uplayRedirect()
      }
    }
    window.addEventListener('message', this.handleUplayMessage, false)

    if (!document.querySelector('[id=ubistatic2]')) {
      const script = document.createElement('script')
      script.src =
        'https://ubistatic2-a.akamaihd.net/uplay-connect/v3/prod/default/sdk/connectSdkPublic.js'
      script.type = 'text/javascript'
      script.id = 'ubistatic2'
      document.body.appendChild(script)
    }
  }

  componentWillUnmount() {
    this.clearUplayAuthTimeout()
    window.removeEventListener('message', this.handleUplayMessage, false)
  }

  clearUplayAuthTimeout = () => {
    if (this.uplayAuthTimeout) {
      clearTimeout(this.uplayAuthTimeout)
      this.uplayAuthTimeout = null
    }
  }

  startUplayAuthTimeout = () => {
    this.clearUplayAuthTimeout()
    this.uplayAuthAttemptId += 1
    const authAttemptId = this.uplayAuthAttemptId

    this.uplayAuthTimeout = setTimeout(() => {
      if (authAttemptId !== this.uplayAuthAttemptId) return

      this.uplayAuthTimeout = null
      this.setState({
        initialized: false,
        loading: false,
        modal: false,
        uplayError: true,
        uplayErrorString: 'error.uplay.5003',
      })
    }, uplayAuthTimeout)

    return authAttemptId
  }

  // modal is buggy because the Uplay's sdk opens twice (based on the Ubisoft games' quantity)
  // this is why we have modalState can be undefined
  toggleModal = (modalState, options = {}) => {
    this.setState(state => {
      const nextModal = modalState === undefined ? !state.modal : modalState

      return {
        modal: nextModal,
        loading: nextModal || options.preserveLoading ? state.loading : false,
      }
    })
  }

  uplayRedirect = () => {
    // Pop-In integration
    const { login } = this.props
    // Close modal
    this.setState({ loading: true }, () => this.toggleModal(false, { preserveLoading: true }))
    const authAttemptId = this.startUplayAuthTimeout()
    const { myAppId, myGenomeId, initialized } = this.state
    if (!initialized && typeof window.Connect !== 'undefined') {
      window.Connect.init({
        env: 'PROD',
        appId: myAppId,
        genomeId: myGenomeId,
        thirdPartyCookiesSupport: false,
      })
      window.Connect.sdk.subscribe(async sdk => {
        const connectSDK = sdk
        await connectSDK.getTicket().subscribe(getTicketResponse => {
          if (authAttemptId !== this.uplayAuthAttemptId) return
          this.clearUplayAuthTimeout()

          if (getTicketResponse.status === 'ok') {
            this.setState({ loading: false, initialized: true }, () =>
              login(getTicketResponse.payload.ticket)
            )
          } else {
            console.error('Error connecting to Uplay - try refreshing the page')
            this.setState({
              initialized: false,
              loading: false,
              uplayError: true,
              uplayErrorString: 'error.uplay.5003',
            })
          }
        })
      })
    } else if (!initialized) {
      this.clearUplayAuthTimeout()
      this.setState({ loading: false })
    } else {
      this.clearUplayAuthTimeout()
      this.setState({ loading: false })
    }
  }

  uplayLogout = () => {
    const { myAppId, myGenomeId } = this.state
    const { logout } = this.props
    this.setState({ loading: true })
    logout()

    const finishLogout = () => {
      this.setState({ loading: false, initialized: false })
    }

    if (typeof window.Connect === 'undefined') {
      finishLogout()
      return
    }

    window.Connect.init({
      env: 'PROD',
      appId: myAppId,
      genomeId: myGenomeId,
      thirdPartyCookiesSupport: false,
    })
    window.Connect.sdk.subscribe(sdk => {
      sdk.getTicket().subscribe(getTicketResponse => {
        if (getTicketResponse.status !== 'ok' || !getTicketResponse.payload) {
          finishLogout()
          return
        }
        const { ticket, sessionId } = getTicketResponse.payload
        sdk.logout(ticket, sessionId).subscribe(() => {
          finishLogout()
        })
      })
    })

    setTimeout(finishLogout, 5000)
  }

  activateGame = () => {
    const {
      uplay: { authenticated },
      activateGame,
      activationKey,
      contentId,
      couponCode,
    } = this.props
    this.setState({ loading: true })
    if (authenticated) {
      activateGame(activationKey, contentId, couponCode)
        .then(() => this.setState({ loading: false, uplaySuccess: true, redeemedLocally: true }))
        .catch(error => {
          if (error.errors && error.errors.errorType) {
            this.setState({
              loading: false,
              uplayError: true,
              uplayErrorString: error.errors.message,
            })
          } else {
            this.setState({
              loading: false,
              uplayError: true,
              uplayErrorString: 'error.global.unexpected',
            })
          }
        })
    } else {
      this.setState({
        loading: false,
        uplayError: true,
        uplayErrorString: 'error.uplay.invalidCredentials',
      })
    }
  }

  toggleUplayError = () => {
    this.setState(state => ({ uplayError: !state.uplayError }))
  }

  toggleUplaySuccess = () => {
    this.setState(state => ({ uplaySuccess: !state.uplaySuccess }))
  }

  renderUplayLogout = () => {
    const {
      uplay: { authenticated },
    } = this.props
    if (authenticated) {
      return (
        <a
          className="logout"
          onClick={this.uplayLogout}
          role="button"
          tabIndex={0}
          aria-label="Logout from Uplay account"
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              this.uplayLogout()
            }
          }}
        >
          <u>
            <FormattedMessage id="uPlay.button.logout" />
          </u>
        </a>
      )
    }
  }

  renderButton = () => {
    const {
      redeemed: redeemedFromProps,
      uplay: { authenticated },
    } = this.props
    const { loading, redeemedLocally } = this.state
    const redeemed = redeemedFromProps || redeemedLocally
    let buttonText = 'uPlay.button.logIn' // Redeem on Uplay with your Ubisoft account
    let buttonClick = () => this.setState({ loading: true }, () => this.toggleModal(true))
    let ariaLabel = 'Log in to Uplay'

    if (redeemed) {
      buttonText = 'uPlay.button.play' // Open Uplay PC (or download it here) to play your game
      buttonClick = () => {
        window.open('https://uplay.ubi.com/')
      }
      ariaLabel = 'Open Uplay website (opens in new window)'
    } else if (authenticated) {
      buttonText = 'uPlay.button.activate' // Activate on Uplay
      buttonClick = () => this.activateGame()
      ariaLabel = 'Activate game on Uplay'
    }

    return (
      <div className="col">
        {loading ? (
          <Loader button />
        ) : (
          <Button
            color="dark"
            block
            outline
            onClick={buttonClick}
            aria-describedby="uplay-integration-status"
            aria-label={ariaLabel}
          >
            <FormattedMessage id={buttonText} />
          </Button>
        )}
        {this.renderUplayLogout()}
      </div>
    )
  }

  render() {
    const { contentName, className, redeemed: redeemedFromProps, uplay } = this.props
    const {
      loading,
      myAppId,
      myGenomeId,
      myNextUrl,
      uplayError,
      uplaySuccess,
      uplayErrorString,
      modal,
      redeemedLocally,
    } = this.state
    const redeemed = redeemedFromProps || redeemedLocally

    if (loading && !modal) {
      return <Loader button />
    }
    return (
      <div className="mb-3">
        <div className="row">{this.renderButton()}</div>
        <div id="uplay-integration-status" className="sr-only">
          {(() => {
            if (redeemed) return 'Game activated successfully'
            if (uplay?.authenticated) return 'Logged into Uplay'
            return 'Not logged in'
          })()}
        </div>
        <MyRewardsErrorModal
          isOpen={uplayError}
          toggle={this.toggleUplayError}
          error={uplayErrorString}
          platform="uplay"
        />
        <MyRewardsSuccessModal
          isOpen={uplaySuccess}
          toggle={this.toggleUplaySuccess}
          contentName={contentName}
          platform="uplay"
        />
        <Modal
          isOpen={modal}
          toggle={() => this.toggleModal()}
          className={className}
          style={{ maxWidth: '580px' }}
          role="dialog"
          aria-labelledby="uplay-login-modal-title"
          aria-describedby="uplay-login-modal-body"
        >
          <ModalHeader
            toggle={() => this.toggleModal()}
            id="uplay-login-modal-title"
            aria-label="Uplay login"
          />
          <ModalBody style={{ minHeight: '800px' }} id="uplay-login-modal-body">
            {modal && (
              <iframe
                className="iFrame"
                title="Uplay authentication iframe"
                src={`https://connect.ubisoft.com/login?appId=${myAppId}&genomeId=${myGenomeId}&lang=en-US&nextUrl=${encodeURIComponent(
                  myNextUrl
                )}`}
                width={480}
                height={720}
              />
            )}
          </ModalBody>
        </Modal>
      </div>
    )
  }
}

const mapStateToProps = state => ({
  uplay: state.uplayReducer,
})

const mapDispatchToProps = dispatch => ({
  activateGame: (...args) => dispatch(uplayActions.activateGame(...args)),
  login: (...args) => dispatch(uplayActions.login(...args)),
  logout: () => dispatch(uplayActions.login()),
  checkAuthenticated: (...args) => dispatch(uplayActions.checkAuthenticated(...args)),
})

export default connect(mapStateToProps, mapDispatchToProps)(UplayIntegration)

UplayIntegration.propTypes = {
  redeemed: PropTypes.bool,
  contentName: PropTypes.string,
  activationKey: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  contentId: PropTypes.number,
  couponCode: PropTypes.string,
}
